import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import {
  practiceAreas,
  leadershipTeam,
  globalOffices,
  partnerEcosystem,
  engagementPaths,
} from "@/mocks/homeData";

function SectionReveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <div className="bg-azosis-dark min-h-screen">
      {/* ========================================== */}
      {/* S1 — HERO */}
      {/* ========================================== */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="/assets/home-hero-bg.webp"
            alt="Cloud technology infrastructure"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-azosis-dark/80"></div>

        {/* Glow */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-azosis-gold/10 rounded-full blur-[180px]"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-12 pb-6 md:pt-16 md:pb-8">
          <div className="max-w-4xl mx-auto text-center">
            <SectionReveal delay={100}>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-8 md:mb-10">
                Trusted technology consulting and managed services - engineered
                in India, delivered globally.
              </h1>
            </SectionReveal>

            <SectionReveal delay={200}>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto mb-6 leading-relaxed">
                Migrating systems. Modernising workspaces. Managing technology
                operations long after go-live. India is where Azosis engineers,
                builds, and delivers — serving clients across four geographies
                from our Bangalore hub.
              </p>
            </SectionReveal>

            <SectionReveal delay={400}>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
              >
                Book a discovery workshop
                <i className="ri-arrow-right-line"></i>
              </Link>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ========================================== */}
      {/* S2 — SERVICES (replaces The Program) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-surface">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                Our Services
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Migration &middot; Workspace &middot; Managed Services
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                Three practice areas. Built and delivered from Bangalore. Every
                engagement starts with your problem.
              </p>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {practiceAreas.map((area, index) => (
              <SectionReveal key={area.id} delay={index * 150}>
                <div className="group bg-azosis-dark border border-azosis-border hover:border-azosis-gold/40 transition-all duration-500 h-full flex flex-col">
                  {/* Header */}
                  <div className="p-8 md:p-10 border-b border-azosis-border">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-azosis-gold font-bold text-lg tracking-wider">
                        {area.id}
                      </span>
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-2">
                      {index === 0
                        ? "Migration"
                        : index === 1
                          ? "Workspace"
                          : "Managed Services"}
                    </h3>
                    <p className="text-azosis-gold text-sm">{area.tagline}</p>
                  </div>

                  {/* Body */}
                  <div className="p-8 md:p-10 flex-1">
                    <p className="text-azosis-textSecondary text-sm leading-relaxed">
                      {index === 0
                        ? "Cloud and system migration - from email to infrastructure. Every cutover on schedule."
                        : index === 1
                          ? "Secure distributed workforces, intelligent automation, and AI-assisted data operations."
                          : "Ongoing managed technology operations - support, optimisation, and adoption, long after go-live."}
                    </p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>

          {/* CTA Button below cards */}
          <SectionReveal delay={400}>
            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
              >
                See All Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ========================================== */}
      {/* S3 — WHO WE ARE (NEW) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                Who We Are
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Technology leadership built for your business.
              </h2>
              <p className="text-azosis-textSecondary text-base md:text-lg max-w-2xl mx-auto">
                Since 2010, Azosis has grown into a trusted technology partner across four geographies. Our India entity - <strong className="text-white font-semibold">Azosis Technologies Private Limited</strong>, Bangalore - is the Engineering Hub that powers global delivery.
              </p>
            </div>
          </SectionReveal>

          
          {/* Leadership Tiles */}
    <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
      {leadershipTeam.map((leader, index) => (
        <SectionReveal key={leader.name} delay={index * 100} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[280px]">
          <div className="group bg-azosis-surface border border-azosis-border hover:border-azosis-gold/40 transition-all duration-500 p-8 text-center h-full flex flex-col items-center rounded-sm">
            {/* Leader Photo */}
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full ring-2 ring-azosis-gold/30 group-hover:ring-azosis-gold/60 ring-offset-4 ring-offset-azosis-surface overflow-hidden mb-6 transition-all duration-500">
              <img
                src={
                  leader.name === 'Ramadurai Radhakrishnan'
                    ? '/assets/Ram.webp'
                    : leader.name === 'Lalita Narayanan'
                      ? '/assets/Lalitha.webp'
                      : '/assets/Usha.webp'
                }
                alt={leader.name}
                className="w-full h-full object-cover object-[center_20%]"
              />
            </div>
                    {/* Name */}
                    <h3 className="text-white font-semibold text-sm md:text-base mb-1">
                      {leader.name}
                    </h3>

                    {/* Role */}
                    <p className="text-azosis-gold text-xs tracking-wide mb-3">
                      {leader.role}
                    </p>

                    {/* Tagline */}
                    <p className="text-azosis-textMuted text-xs leading-relaxed">
                      {leader.tagline}
                    </p>
                  </div>
                </SectionReveal>
              ))}
          </div>

          {/* Text link below tiles */}
          <SectionReveal delay={400}>
            <div className="text-center mt-12">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-azosis-gold text-sm hover:text-azosis-goldLight transition-colors"
              >
                Meet the team
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Partner Ecosystem - directly below leadership */}
      <section className="py-8 md:py-12 bg-azosis-surface border-y border-azosis-border">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-8 md:mb-12">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase">
                Partner Ecosystem
              </span>
            </div>
          </SectionReveal>
          <SectionReveal delay={100}>
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
              {partnerEcosystem.map((partner, index) => (
                <span key={partner} className="flex items-center gap-x-2">
                  <span className="text-azosis-textMuted text-sm md:text-base font-medium tracking-wide">
                    {partner}
                  </span>
                  {index < partnerEcosystem.length - 1 && (
                    <span className="text-azosis-textMuted text-sm md:text-base">
                      ·
                    </span>
                  )}
                </span>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ========================================== */}
      {/* S4 — OUR APPROACH (NEW) */}
      {/* ========================================== */}
      <section className="py-8 md:py-12 bg-azosis-dark">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <SectionReveal>
            <div className="text-center mb-16 md:mb-20">
              <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                Our Approach
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                From first conversation to live in production.
              </h2>
            </div>
          </SectionReveal>

          {/* 3 Horizontal Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto">
            {engagementPaths.map((step, index) => (
              <SectionReveal key={step.step} delay={index * 150}>
                <div className="text-center md:text-left">
                  {/* Number Badge */}
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-azosis-gold/50 text-azosis-gold font-display font-bold text-lg mb-5">
                    {step.step}
                  </span>

                  {/* Title */}
                  <h3 className="text-white font-semibold text-base md:text-lg mb-3">
                    {step.title}
                  </h3>

                  {/* One-line description */}
                  <p className="text-azosis-textMuted text-sm leading-relaxed">
                    {index === 0
                      ? "Understanding your business goals and technical challenges."
                      : index === 1
                        ? "Validating ideas through practical implementation."
                        : "Long-term delivery, optimisation, and operational success."}
                  </p>
                </div>
              </SectionReveal>
            ))}
          </div>

          {/* CTA #2 — FINAL CTA */}
          <SectionReveal delay={450}>
            <div className="text-center mt-16 md:mt-20">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
              >
                Book a discovery workshop
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

    

     
    </div>
  );
}
